import { Enum } from "enum-plus";
import { z } from "zod/v4";
// =======================================
// EMERGENCY LEVEL
// =======================================
export const EmergencyLvlEnum = Enum({
    Urgent: {
        value: 0,
        label: "Urgent",
    },
    AFewDays: {
        value: 1,
        label: "A few days",
    },
    NotUrgent: {
        value: 2,
        label: "Not urgent",
    },
});
// =======================================
// INTENSITÉ DOULEUR
// =======================================
export const intensitéDouleurChoices = [
    {
        value: "sensibilité",
        text: "Sensibilité",
    },
    {
        value: "modérée",
        text: "Douleur modérée",
    },
    {
        value: "lourdeur",
        text: "Lourdeur",
    },
    {
        value: "déchargeBrève",
        text: "Décharge électrique intense brève",
    },
    {
        value: "forteDouleur",
        text: "Forte douleur",
    },
];
// =======================================
// DOULEUR PROVOQUÉE
// =======================================
export const douleurProvoquéeConfig = {
    title: "Par quoi est déclenchée la douleur ?",
    questions: [
        { id: "coldCheck", text: "Le froid", value: "coldPain" },
        { id: "hotCheck", text: "Le chaud", value: "hotPain" },
        { id: "chewingCheck", text: "La mastication", value: "chewingPain" },
        {
            id: "tongueCheck",
            text: "Le contact de la langue",
            value: "tongueContactPain",
        },
        {
            id: "spontaneousCheck",
            text: "Ca fait mal sans rien faire",
            value: "spontaneousPain",
        },
    ],
};
// =======================================
// FRÉQUENCE DOULEUR
// =======================================
export const dernièreFoisChoices = [
    { value: "maintenant", text: "Maintenant" },
    { value: "aujourd'hui", text: "Aujourd'hui" },
    { value: "hier", text: "Hier" },
    { value: "cette_semaine", text: "Cette semaine" },
    { value: "semaine_dernière", text: "La semaine dernière" },
    { value: "ce_mois", text: "Ce mois-ci" },
    { value: "plus_ancien", text: "Il y a plus d'un mois" },
];
export const évolutionChoices = [
    { value: "diminue", text: "La douleur diminue" },
    { value: "stable", text: "La douleur reste stable" },
    { value: "augmente", text: "La douleur augmente" },
    { value: "variable", text: "C'est variable" },
];
export const duréeChoices = [
    { value: "un_jour", text: "Un jour" },
    { value: "quelques_jours", text: "Quelques jours" },
    { value: "une_semaine", text: "Une semaine" },
    { value: "quelques_semaines", text: "Quelques semaines" },
    { value: "un_mois", text: "Un mois" },
    { value: "plusieurs_mois", text: "Plusieurs mois" },
    { value: "plus_six_mois", text: "Plus de 6 mois" },
];
export const fréquenceQuotidienneChoices = [
    { value: "tout_le_temps", text: "Ça fait mal tout le temps" },
    { value: "plusieurs_fois_par_heure", text: "Plusieurs fois par heure" },
    { value: "quelques_fois_par_jour", text: "Quelques fois par jour" },
    { value: "une_fois_par_jour", text: "Une fois par jour" },
    { value: "pas_tous_les_jours", text: "Pas tous les jours" },
];
// =======================================
// ANTALGIQUES
// =======================================
export const didYouTakePainKillersChoices = [
    { value: "yes", text: "Oui" },
    { value: "no", text: "Non" },
];
export const whatPainKillerDidYouUseChoices = [
    { value: "paracetamol", text: "Paracétamol/Doliprane/Dafalgan/Efferalgan" },
    { value: "ibuprofene", text: "Ibuprofène/Advil/Nurofen/Spifen/Spedifen" },
    { value: "codeine", text: "Codéine/Codoliprane/Codafalgan" },
    { value: "tramadol", text: "Tramadol/Topalgic" },
    { value: "other", text: "Autre" },
];
export const wasItEffectiveChoices = [
    { value: "yes", text: "Oui" },
    { value: "no", text: "Non" },
];
// =======================================
// RÉVEIL NOCTURNE
// =======================================
export const réveilNocturneChoices = [
    { value: "yes", text: "Oui" },
    { value: "no", text: "Non" },
];
// =======================================
// SCHÉMAS ZOD
// =======================================
// Schéma pour IntensitéValue - utilise les valeurs déjà définies
export const intensitéValueSchema = z.enum(intensitéDouleurChoices.map((choice) => choice.value));
// Schéma pour DouleurValue - utilise les valeurs déjà définies
export const douleurValueSchema = z.enum(douleurProvoquéeConfig.questions.map((q) => q.value));
// Schéma pour FrequenceDouleurState - utilise les valeurs déjà définies
export const frequenceDouleurStateSchema = z.object({
    dernièreFois: z.enum(dernièreFoisChoices.map((choice) => choice.value)),
    évolution: z.enum(évolutionChoices.map((choice) => choice.value)),
    durée: z.enum(duréeChoices.map((choice) => choice.value)),
    fréquenceQuotidienne: z.enum(fréquenceQuotidienneChoices.map((choice) => choice.value)),
});
// Schéma pour PainKillersState (union discriminée) - utilise les valeurs déjà définies
export const painKillersStateSchema = z.discriminatedUnion("didYouTakePainKillers", [
    z.object({
        didYouTakePainKillers: z.literal(true),
        whatPainKillerDidYouUse: z.enum(whatPainKillerDidYouUseChoices.map((choice) => choice.value)),
        wasItEffective: z.boolean(),
    }),
    z.object({
        didYouTakePainKillers: z.literal(false),
    }),
]);
// Schéma principal pour QuestionnaireDouleurFullState
export const questionnaireDouleurFullStateSchema = z.object({
    intensité: intensitéValueSchema,
    déclencheurs: z.set(douleurValueSchema),
    fréquence: frequenceDouleurStateSchema,
    antalgiques: painKillersStateSchema,
    réveilNocturne: z.boolean(),
});
//# sourceMappingURL=QuestionnaireDouleur.js.map